import { makeStyles } from "@mui/styles";
import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { SubmitLock } from "components/Form/components/SubmitLock/SubmitLock";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { CopyToClipboardFormComponent } from "components/Form/components/CopyToClipboard/CopyToClipboardFormComponent";
import { Form } from "components/Form/components/Form/Form";
import { StoreProviderForm } from "components/Form/StoreProviderForm";
import { TextFormComponent } from "components/Form/components/Text/TextFormComponent";
import { requiredValidator } from "components/Form/Form.utils";
import NotificationService from "components/Notification/Notification.service";
import { watchEnableGitExport } from "./watchEnableGitExport";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { Popover } from "components/Popover/Popover";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { forceRecordUpdate } from "containers/Resources/components/CommonActions/CommonActions";
import { DefaultTrackInGitField } from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/DefaultTrackInGitField";
import { ForceTrackInGitField } from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/ForceTrackInGitField";
import { MaxTrackedRecordBytesField } from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/MaxTrackedRecordBytesField";
import {
    MaxTrackedSizeOverLimitDialog,
    OverLimitRecord,
} from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/MaxTrackedSizeOverLimitDialog";
import {
    bytesToKb,
    kbFieldToBytes,
    shouldPreflightMaxTrackedBytes,
} from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/maxTrackedSize";
import { submitSingleRecordForm } from "containers/Resources/screens/ResourcesSingle/utils";
import { useEffect, useState } from "react";
import { Field, Form as FinalForm } from "react-final-form";
import { useDispatch } from "react-redux";
import "./ManageGitModal.scss";

/**
 * The unified "Manage git" dialog for a container (project / bundle / workspace).
 *
 * One cohesive modal that replaces the previously-scattered git dropdown entries
 * (Manage git branch / Manage git repository / Disable git) and absorbs the three
 * git-tracking container settings (default/force track in git, max tracked record
 * size) that used to live, save-on-blur, in the Details sidebar.
 *
 * Two states, selected by whether git is enabled (`!!resourceData.branch_name`):
 *  - State A (git disabled): repository name + branch name -> Enable git.
 *  - State B (git enabled): Repository / Branch / Tracking sections + a destructive
 *    Disable-git action, all behind one explicit Save.
 *
 * Save discipline (Stefan's hard requirement): every control edits LOCAL react-
 * final-form state only — NOTHING persists until Save. The reused tracking-field
 * components persist via injected onChange/handleSubmit callbacks; omitting those
 * here makes them edit form state only. This is the root-cause fix for the
 * flash-and-close over-limit-dialog bug (a field blur used to persist AND tear
 * down the Details popover). Because this modal owns its open-state and is not
 * dismissed by a field blur, the layered over-limit dialog now mounts and stays.
 *
 * Built on the canonical `Popover` dialog shell (ui-design-principle-modals-
 * dialogs: centered, bold header, no ×, Cancel sole dismissal, 24px padding,
 * overflow:visible Paper so the branch react-select can spill) with the shared
 * `Button`/`CancelButton` (ui-design-principle-buttons: one `primary`, co-equal
 * peers `dark`, destructive `danger`).
 */

export interface ManageGitModalProps {
    isOpen: boolean;
    /** Typed-lowercase container REST type: "project" | "bundle" | "workspace". */
    resourceType: string;
    /** The container record (id, name, branch_name, git_url, tracking settings). */
    resourceData: any;
    /** Current user id — used to tailor the HTTPS-clone PAT note. */
    userId?: string;
    onClose: () => void;
    afterAction?: () => void;
}

interface RemoteUrls {
    ssh?: string;
    https?: string;
}

const useDialogStyles = makeStyles(() => ({
    rootStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
    },
    paperStyle: {
        maxHeight: "85vh",
        overflow: "visible",
    },
}));

export const ManageGitModal = ({
    isOpen,
    resourceType,
    resourceData,
    userId,
    onClose,
    afterAction,
}: ManageGitModalProps) => {
    const dispatch = useDispatch();
    const classes = useDialogStyles();

    const gitEnabled = !!resourceData?.branch_name;
    const containerId = resourceData?.id;

    // State-B async context (clone URLs, branch list, PAT) — loaded on open.
    const [loaded, setLoaded] = useState(false);
    const [urls, setUrls] = useState<RemoteUrls>({});
    const [branchList, setBranchList] = useState<{ label: string; value: string }[]>([]);
    const [hasPat, setHasPat] = useState(false);

    // Disable-git inline confirm step (foot of State B).
    const [confirmDisable, setConfirmDisable] = useState(false);
    const [disabling, setDisabling] = useState(false);

    // Layered over-limit pre-flight dialog state.
    const [overLimit, setOverLimit] = useState<{
        open: boolean;
        records: OverLimitRecord[];
        scanned: number;
        truncated: boolean;
        candidateBytes: number;
        persist: (bytes: number) => void;
    } | null>(null);

    const getRepoName = (gitUrl?: string | null) => {
        if (!gitUrl) {
            return "";
        }
        const parts = gitUrl.split("/");
        return parts[parts.length - 1].replace(".git", "");
    };

    useEffect(() => {
        if (!isOpen || !gitEnabled || loaded) {
            return;
        }
        let cancelled = false;
        const repoName = getRepoName(resourceData?.git_url);
        const urlsPromise = restClient.get(
            `${resourceType}/${containerId}/git/remote_url?repository_name=${encodeURIComponent(
                repoName
            )}`
        );
        const branchPromise = restClient.get(`${resourceType}/${containerId}/git/branch`);
        const patPromise = userId
            ? restClient.get(`user/${userId}?fields=has_pat`)
            : Promise.resolve(null);

        Promise.allSettled([urlsPromise, branchPromise, patPromise]).then(
            ([urlsRes, branchRes, patRes]) => {
                if (cancelled) {
                    return;
                }
                const nextUrls: RemoteUrls =
                    urlsRes.status === "fulfilled"
                        ? (urlsRes.value as any)?.data ?? {}
                        : { ssh: resourceData?.git_url, https: "" };
                setUrls(nextUrls);
                if (branchRes.status === "fulfilled") {
                    const list = ((branchRes.value as any)?.data ?? []).map((b: any) => ({
                        label: b.name,
                        value: b.name,
                    }));
                    setBranchList(list);
                }
                const user =
                    patRes && (patRes as any).status === "fulfilled"
                        ? (patRes as any).value?.data
                        : null;
                setHasPat(!!user?.user?.has_pat);
                setLoaded(true);
            }
        );
        return () => {
            cancelled = true;
        };
    }, [isOpen, gitEnabled, loaded, resourceType, containerId, userId, resourceData]);

    const notifyError = (error: any) =>
        NotificationService.error(<RestErrorNotification error={error} />);

    // --- State A: enable git (create repo) -----------------------------------
    const enableGit = (formData: any) =>
        submitSingleRecordForm({
            formData: {
                id: containerId,
                branch_name: formData.branch_name,
                repository_name: formData.repository_name,
            },
            resourceConfig: {},
            dispatch,
            resourceName: resourceType,
        })
            ?.then((result: any) => {
                NotificationService.success("Git repository created");
                forceRecordUpdate(dispatch);
                // Large containers export their records in the background; show progress.
                watchEnableGitExport({
                    resourceType,
                    containerId,
                    label: `${resourceType} "${resourceData?.name}"`,
                    onFinished: () => forceRecordUpdate(dispatch),
                });
                afterAction?.();
                onClose();
                return result;
            })
            ?.catch((error: any) => notifyError(error));

    // --- State B: disable git ------------------------------------------------
    const disableGit = () => {
        setDisabling(true);
        new Promise((resolve, reject) => {
            dispatch({
                type: resourcesActions.START_UPDATE_RESOURCE,
                promise: { resolve, reject },
                resourceName: resourceType,
                variables: { id: containerId, branch_name: null },
            });
        })
            .then(() => {
                NotificationService.success("Git disabled");
                forceRecordUpdate(dispatch);
                afterAction?.();
                onClose();
            })
            .catch((error) => notifyError(error))
            .finally(() => setDisabling(false));
    };

    // --- State B: persist the changed git settings on Save -------------------
    const persist = (variables: Record<string, any>) => {
        if (Object.keys(variables).length === 0) {
            onClose();
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            dispatch({
                type: resourcesActions.START_UPDATE_RESOURCE,
                promise: { resolve, reject },
                resourceName: resourceType,
                variables: { id: containerId, ...variables },
            });
        })
            .then(() => {
                NotificationService.success("Git settings saved");
                forceRecordUpdate(dispatch);
                afterAction?.();
                onClose();
            })
            .catch((error) => notifyError(error));
    };

    const saveSettings = (formData: any, form: any) => {
        const changed = Object.keys(form.getState().dirtyFields ?? {});
        const variables: Record<string, any> = {};
        if (changed.includes("repository_name")) {
            variables.repository_name = formData.repository_name;
        }
        if (changed.includes("branch_name")) {
            variables.branch_name = formData.branch_name;
        }
        if (changed.includes("default_track_in_git")) {
            variables.default_track_in_git = formData.default_track_in_git;
        }
        if (changed.includes("force_track_in_git")) {
            variables.force_track_in_git = formData.force_track_in_git;
        }

        const candidateBytes = kbFieldToBytes(formData.max_tracked_record_bytes);
        if (changed.includes("max_tracked_record_bytes")) {
            variables.max_tracked_record_bytes = candidateBytes;
        }

        // Pre-flight the tracked-record size limit BEFORE persisting (only when a
        // concrete lower limit is being set on a container).
        if (shouldPreflightMaxTrackedBytes(true, changed, candidateBytes)) {
            return restClient
                .get(`${resourceType}/${containerId}/git/oversized-tracked-records`, {
                    params: { by: "id", limit: candidateBytes },
                })
                .then((response: any) => {
                    const over: OverLimitRecord[] = response?.data?.over_limit ?? [];
                    if (over.length === 0) {
                        return persist(variables);
                    }
                    setOverLimit({
                        open: true,
                        records: over,
                        scanned: response?.data?.scanned ?? over.length,
                        truncated: !!response?.data?.truncated,
                        candidateBytes: candidateBytes as number,
                        persist: (bytes: number) => {
                            setOverLimit(null);
                            persist({ ...variables, max_tracked_record_bytes: bytes });
                        },
                    });
                    return undefined;
                })
                .catch((error: any) => notifyError(error));
        }

        return persist(variables);
    };

    if (!isOpen) {
        return null;
    }

    const headerTitle = gitEnabled
        ? `Manage git for ${resourceType} "${resourceData?.name}"`
        : `Enable git for ${resourceType} "${resourceData?.name}"`;

    const stateA = (
        <FinalForm onSubmit={enableGit} initialValues={{ branch_name: "", repository_name: "" }}>
            {({ handleSubmit, pristine, hasValidationErrors, submitting }) => (
                <Form onSubmit={handleSubmit}>
                    <SubmitLock locked={submitting}>
                        <Form.Row>
                            <Field
                                name="repository_name"
                                component={TextFormComponent}
                                validate={requiredValidator}
                                fieldProps={{ label: "Repository name:" }}
                                fieldAttr={{ placeholder: "enter repository name", required: true }}
                            />
                        </Form.Row>
                        <Form.Row>
                            <Field
                                name="branch_name"
                                component={TextFormComponent}
                                validate={requiredValidator}
                                fieldProps={{ label: "Branch name:" }}
                                fieldAttr={{ placeholder: "enter branch name", required: true }}
                            />
                        </Form.Row>
                    </SubmitLock>
                    <div className="manageGit__buttons">
                        <CancelButton onClick={onClose} disabled={submitting} />
                        <Button
                            submit
                            variation="primary"
                            label="Enable git"
                            disabled={pristine || hasValidationErrors}
                            loading={submitting}
                        />
                    </div>
                </Form>
            )}
        </FinalForm>
    );

    const stateB = !loaded ? (
        <div className="manageGit__loading">Loading…</div>
    ) : (
        <FinalForm
            onSubmit={saveSettings}
            initialValues={{
                repository_name: getRepoName(urls.ssh ?? resourceData?.git_url),
                branch_name: resourceData?.branch_name,
                git_url: urls.ssh ?? resourceData?.git_url ?? "",
                clone_ssh: urls.ssh ? `git clone ${urls.ssh}` : "",
                clone_https: urls.https ? `git clone ${urls.https}` : "",
                default_track_in_git: !!resourceData?.default_track_in_git,
                force_track_in_git: !!resourceData?.force_track_in_git,
                max_tracked_record_bytes:
                    resourceData?.max_tracked_record_bytes != null
                        ? bytesToKb(resourceData.max_tracked_record_bytes)
                        : "",
            }}
        >
            {({ handleSubmit, pristine, hasValidationErrors, submitting }) => (
                <Form onSubmit={handleSubmit}>
                    <SubmitLock locked={submitting || disabling}>
                        <div className="manageGit__section">
                            <Form.Row>
                                <Field
                                    name="repository_name"
                                    component={TextFormComponent}
                                    validate={requiredValidator}
                                    fieldProps={{ label: "Repository name:" }}
                                    fieldAttr={{ required: true }}
                                />
                            </Form.Row>
                            <Form.Row>
                                <Field
                                    name="clone_ssh"
                                    component={CopyToClipboardFormComponent}
                                    fieldProps={{ label: "Clone via SSH:" }}
                                />
                            </Form.Row>
                            <Form.Row>
                                <Field
                                    name="clone_https"
                                    component={CopyToClipboardFormComponent}
                                    fieldProps={{ label: "Clone via HTTPS:" }}
                                />
                            </Form.Row>
                            <div className="manageGit__note">
                                {hasPat
                                    ? "HTTPS cloning requires your Personal Access Token (PAT)."
                                    : "HTTPS cloning requires a Personal Access Token (PAT). Create one on your user page under Git HTTPS token."}
                            </div>
                        </div>

                        <div className="manageGit__section">
                            <Form.Row>
                                <Field
                                    name="branch_name"
                                    component={AutocompleteFormComponent}
                                    validate={requiredValidator}
                                    fieldProps={{
                                        label: "Branch name:",
                                        isCreatable: true,
                                        options: { list: branchList },
                                    }}
                                    fieldAttr={{ required: true }}
                                />
                            </Form.Row>
                        </div>

                        <div className="manageGit__section">
                            <Form.Row>
                                <DefaultTrackInGitField
                                    label="Default track in git"
                                    initialValue={!!resourceData?.default_track_in_git}
                                />
                            </Form.Row>
                            <Form.Row>
                                <ForceTrackInGitField
                                    label="Force track in git"
                                    initialValue={!!resourceData?.force_track_in_git}
                                />
                            </Form.Row>
                            <Form.Row>
                                <MaxTrackedRecordBytesField
                                    label="Max tracked record size"
                                    initialValue={
                                        resourceData?.max_tracked_record_bytes != null
                                            ? bytesToKb(resourceData.max_tracked_record_bytes)
                                            : ""
                                    }
                                />
                            </Form.Row>
                        </div>

                        <div className="manageGit__section manageGit__disable">
                            {confirmDisable ? (
                                <div className="manageGit__disableConfirm">
                                    <div className="manageGit__note">
                                        Disabling git deletes the git repository and all its files,
                                        including history. The {resourceType} and its resources stay in
                                        your workspace. You can re-enable git later.
                                    </div>
                                    <div className="manageGit__disableConfirmButtons">
                                        <CancelButton onClick={() => setConfirmDisable(false)} />
                                        <Button
                                            variation="danger"
                                            label="Disable git & delete repository"
                                            loading={disabling}
                                            onClick={disableGit}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <Button
                                    variation="danger"
                                    label="Disable git"
                                    onClick={() => setConfirmDisable(true)}
                                />
                            )}
                        </div>
                    </SubmitLock>

                    <div className="manageGit__buttons">
                        <CancelButton onClick={onClose} disabled={submitting || disabling} />
                        <Button
                            submit
                            variation="primary"
                            label="Save"
                            disabled={pristine || hasValidationErrors || disabling}
                            loading={submitting}
                        />
                    </div>
                </Form>
            )}
        </FinalForm>
    );

    return (
        <>
            <Popover
                isOpen={isOpen}
                classes={{ root: classes.rootStyle, paper: classes.paperStyle }}
                rootClassName="manageGit__popover"
                contentClassName="manageGit__content"
                anchorReference="none"
                closeOnInsideClick={false}
                closeOnOutsideClick={false}
            >
                <StoreProviderForm>
                    <div className="manageGit__header">{headerTitle}</div>
                    {gitEnabled ? stateB : stateA}
                </StoreProviderForm>
            </Popover>
            {overLimit?.open && (
                <MaxTrackedSizeOverLimitDialog
                    isOpen
                    resourceName={resourceType}
                    containerId={containerId}
                    containerType={resourceType}
                    candidateBytes={overLimit.candidateBytes}
                    initialRecords={overLimit.records}
                    scanned={overLimit.scanned}
                    truncated={overLimit.truncated}
                    forceTrackInGit={!!resourceData?.force_track_in_git}
                    onSetLimit={overLimit.persist}
                    onCancel={() => setOverLimit(null)}
                />
            )}
        </>
    );
};
